import { useAppSelector, useAppDispatch } from "@common-reducers/hooks/store.hook";
import { getAllEntitiesIdsAndTypeForCalendarSidebar } from "@common-reducers/UserMessageSelectors";
import { userMessagesSelectByQuery } from "@common-reducers/DBServiceThunks";
import { getEffectiveMessageStatus } from "@common-components/message-status/message-status-config";
import { deleteUserMessage } from "@common/thunks/UserMessagesThunks";
import { getPendingAndRuningCampaigns } from '@common/reducers/CampaignThunks';
import { isExtensionContext } from '@common/services/detect-context';
import { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { VariableSizeList as List } from 'react-window';
import { Trash2, Loader2, CalendarPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { plannerRowHeight, ROW_HEIGHT } from './planner-row-height';
import { useListPausedSeriesQuery } from '@common/reducers/backend-api/backend-api';
import { PlannerVirtualRow, type RowData, type SidebarItem, type SidebarRow } from './planner-virtual-row';
import classes from "./scheduled-contact-list.module.scss";
import clearFailed from "./clear-failed.module.scss";
import ScheduledMessagesEmptyState from './scheduledMessagesEmptyState';
import { handleNewMessageDialogThunk } from '@common-reducers/CalendarThunks';
import { normalizePlannerFilters, plannerEmptyReason } from '@common/services/planner-filters';
import { setCalendarSidebarFilters } from '@common/reducers/CalendarReducer';
import { isScheduleAgainEligible, pruneSelection } from '@common/services/schedule-again';
import { ScheduleAgainContext, type ScheduleAgainContextValue } from './schedule-again-context';
import ScheduleAgainDialog from './schedule-again-dialog';
import scheduleAgainBar from "./schedule-again-bar.module.scss";

/** Stable empty reference so the `rows` memo below is not invalidated every render. */
const EMPTY_ITEMS: never[] = [];

export default function ExtensionSidebarContactList() {
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation(namespace);
    // react-window's `direction` prop defaults to 'ltr' and it writes that as an INLINE
    // style on the scroller, so in Hebrew the whole planner list was pinned LTR while its
    // own "clear failed" header above it rendered RTL — avatars, status chips and the
    // "Upcoming" divider all sat on the wrong side of the one column they share.
    const listDirection = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl' ? 'rtl' : 'ltr';
    const listRef = useRef<List>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    // The virtualized <List> is sized from THIS inner area (not the outer container), so the
    // "clear failed" header can take its own height without pushing the list into overflow.
    const listAreaRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [confirmClear, setConfirmClear] = useState(false);
    const [clearing, setClearing] = useState(false);
    // FE#444 — "Schedule again": select mode, the ticked sent messages, and what the dialog is open for.
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
    const [dialogIds, setDialogIds] = useState<readonly string[]>([]);

    const searchToken = useAppSelector(state => state.WhatsAppReducer.extensionSidebarSearchToken);
    const combinedGroupedMessagesAndCampaignsArray = useAppSelector(state =>
        getAllEntitiesIdsAndTypeForCalendarSidebar(state, searchToken)
    );
    const storedFilters = useAppSelector(state => state.CalendarReducer.calendarSidebarFilters);
    const filters = useMemo(() => normalizePlannerFilters(storedFilters), [storedFilters]);
    const allMessages = useAppSelector(state => userMessagesSelectByQuery(state.DBUserMessageReducer, {}));

    // Effective status per message id — shared by the "failed only" filter (#122)
    // and the "Clear failed" affordance (#161).
    const statusById = useMemo(
        () => new Map<string, string | undefined>(
            allMessages.map((m: any) => [m._id, getEffectiveMessageStatus(m)])
        ),
        [allMessages]
    );

    // The FILTERING now happens once, inside the selector, through the shared
    // planner-filters predicate the calendar grid uses too — so the two surfaces
    // cannot disagree about the same toggle state again. What arrives here is
    // already filtered; this component only lays it out.
    const filteredItems = combinedGroupedMessagesAndCampaignsArray ?? EMPTY_ITEMS;

    // How much the planner would show with NO filters at all. Drives the empty state's
    // "your filters hid it" vs "you have nothing scheduled" distinction, and the panel's
    // "showing X of Y" readout — both of which need the UNfiltered total, which the
    // filtered selector by construction cannot report.
    const unfilteredCount = useMemo(
        () => allMessages.filter((m: any) => m && m.deleted !== true).length,
        [allMessages],
    );

    // Split the (already now-anchored) list into an UPCOMING block and a PAST
    // block, separated by a labelled divider. The selector sorts upcoming-first
    // ascending then past descending; the divider only appears when BOTH blocks
    // are non-empty — a lone "Upcoming" header over an all-future list is noise.
    const rows = useMemo<SidebarRow[]>(() => {
        const upcoming = filteredItems.filter(i => (i as SidebarItem).isUpcoming);
        const past = filteredItems.filter(i => !(i as SidebarItem).isUpcoming);
        if (upcoming.length === 0 || past.length === 0) {
            return filteredItems.map(item => ({ kind: 'item' as const, item }));
        }
        return [
            { kind: 'divider' as const, label: t('calendar.upcoming', { defaultValue: 'Upcoming' }) },
            ...upcoming.map(item => ({ kind: 'item' as const, item })),
            { kind: 'divider' as const, label: t('calendar.past', { defaultValue: 'Earlier' }) },
            ...past.map(item => ({ kind: 'item' as const, item })),
        ];
    }, [filteredItems, t]);

    // Memoize item data for the list: a new `itemData` would defeat every
    // memoised row (see planner-virtual-row.tsx), so it changes only with `rows`.
    const itemData = useMemo<RowData>(() => ({ rows }), [rows]);
    // #508 — paused rows carry an extra controls line, so they are taller.
    const { data: pausedEntries } = useListPausedSeriesQuery();
    const pausedRowIds = useMemo<ReadonlySet<string>>(() => new Set((pausedEntries ?? []).map(e => e.rowMessageId)), [pausedEntries]);
    const itemSize = useCallback(
        (index: number) => plannerRowHeight(rows[index], pausedRowIds),
        [rows, pausedRowIds],
    );

    // VariableSizeList caches measured offsets — they must be dropped whenever
    // the row layout (counts / divider positions) changes.
    useEffect(() => {
        listRef.current?.resetAfterIndex(0, false);
    }, [rows, pausedRowIds]);

    // Failed/expired standalone messages currently visible in the list. Campaign rows are
    // excluded (they're managed via the campaign, not deleted as one-off messages).
    const failedMessageIds = useMemo(() => {
        return filteredItems
            .filter(item => item.type === 'message')
            .map(item => item._id)
            .filter(id => {
                const s = statusById.get(id);
                return s === 'failed' || s === 'expired';
            });
    }, [filteredItems, statusById]);

    // FE#444 — the sent messages on screen that can be run again, in list order.
    const messagesById = useMemo(() => new Map(allMessages.map(m => [m._id, m])), [allMessages]);
    const scheduleAgainIds = useMemo(
        () => filteredItems
            .filter(item => item.type === 'message')
            .map(item => item._id)
            .filter(id => {
                const m = messagesById.get(id);
                return !!m && isScheduleAgainEligible(m);
            }),
        [filteredItems, messagesById],
    );

    // A filter or refresh can take rows away; never keep a tick on something no longer listed.
    useEffect(() => {
        setSelected(prev => pruneSelection(prev, scheduleAgainIds));
        if (scheduleAgainIds.length === 0) setSelectMode(false);
    }, [scheduleAgainIds]);

    const scheduleAgainContext = useMemo<ScheduleAgainContextValue>(() => ({
        selectMode,
        selected,
        toggle: id => setSelected(prev => {
            const next = new Set(prev);
            if (!next.delete(id)) next.add(id);
            return next;
        }),
        openFor: id => setDialogIds([id]),
    }), [selectMode, selected]);

    const dialogMessages = useMemo(
        () => dialogIds.flatMap(id => {
            const m = messagesById.get(id);
            return m ? [m] : [];
        }),
        [dialogIds, messagesById],
    );

    const leaveSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

    useEffect(() => {
        dispatch(getPendingAndRuningCampaigns());
    }, [dispatch]);

    // A cleared list should reset the confirm affordance.
    useEffect(() => {
        if (failedMessageIds.length === 0) setConfirmClear(false);
    }, [failedMessageIds.length]);

    const handleClearFailed = async () => {
        if (!failedMessageIds.length || clearing) return;
        setClearing(true);
        try {
            await Promise.all(failedMessageIds.map(id => dispatch(deleteUserMessage(id))));
        } finally {
            setClearing(false);
            setConfirmClear(false);
        }
    };

    // Reset scroll position when filters change
    useEffect(() => {
        listRef.current?.scrollTo(0);
    }, [filters.showMessages, filters.showCampaigns, filters.showStatuses, filters.showFailed, filters.showSent, filters.groupsOnly]);

    // Track the LIST AREA dimensions (below the optional header) using ResizeObserver
    useLayoutEffect(() => {
        const area = listAreaRef.current;
        if (!area) return;

        const updateDimensions = () => {
            const { width, height } = area.getBoundingClientRect();
            setDimensions({ width, height });
        };

        // Initial measurement
        updateDimensions();

        // Watch for size changes
        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(area);

        return () => resizeObserver.disconnect();
    }, []);

    const isExtension = isExtensionContext();

    return (
        <div
            ref={containerRef}
            className={classes.contactListContainer}
            style={{
                height: isExtension ? 'calc(100% - 49px)' : undefined,
                flex: !isExtension ? 1 : undefined,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}>
            {filteredItems.length > 0 && failedMessageIds.length > 0 && (
                <div className={clearFailed.bar}>
                    {!confirmClear ? (
                        <button
                            type="button"
                            onClick={() => setConfirmClear(true)}
                            className={clearFailed.trigger}>
                            <Trash2 size={13} className={clearFailed.triggerIcon} />
                            {t('scheduledMessages.clearFailed', { defaultValue: 'Clear failed' })} ({failedMessageIds.length})
                        </button>
                    ) : (
                        <div className={clearFailed.confirm}>
                            <span className={clearFailed.confirmQuestion}>
                                {/* One key, not "Delete" + a number + "?" glued together:
                                    the pieces assumed English word order, so every RTL
                                    locale rendered the question inside out. */}
                                {t('scheduledMessages.clearFailedConfirm', {
                                    count: failedMessageIds.length,
                                    defaultValue: 'Delete {{count}}?',
                                })}
                            </span>
                            <button
                                type="button"
                                disabled={clearing}
                                onClick={handleClearFailed}
                                className={clearFailed.confirmDelete}>
                                {clearing && <Loader2 size={12} className={`animate-spin ${clearFailed.spinner}`} />}
                                {t('common.delete', { defaultValue: 'Delete' })}
                            </button>
                            <button
                                type="button"
                                disabled={clearing}
                                onClick={() => setConfirmClear(false)}
                                className={clearFailed.confirmCancel}>
                                {t('common.cancel', { defaultValue: 'Cancel' })}
                            </button>
                        </div>
                    )}
                </div>
            )}
            {scheduleAgainIds.length > 0 && (
                <div className={scheduleAgainBar.bar}>
                    {!selectMode ? (
                        <button type="button" className={scheduleAgainBar.link} onClick={() => setSelectMode(true)}>
                            <CalendarPlus size={13} />
                            {t('scheduleAgain.select', { defaultValue: 'Select sent to schedule again' })}
                        </button>
                    ) : (
                        <>
                            <span className={scheduleAgainBar.count}>
                                {t('scheduleAgain.selected', { count: selected.size, defaultValue: '{{count}} selected' })}
                            </span>
                            <button
                                type="button"
                                className={scheduleAgainBar.link}
                                onClick={() => setSelected(new Set(scheduleAgainIds))}>
                                {t('scheduleAgain.selectAll', { defaultValue: 'Select all' })}
                            </button>
                            <button type="button" className={scheduleAgainBar.link} onClick={leaveSelectMode}>
                                {t('common.cancel', { defaultValue: 'Cancel' })}
                            </button>
                            <button
                                type="button"
                                className={scheduleAgainBar.primary}
                                disabled={selected.size === 0}
                                onClick={() => setDialogIds(scheduleAgainIds.filter(id => selected.has(id)))}>
                                {t('scheduleAgain.action', { defaultValue: 'Schedule again' })}
                            </button>
                        </>
                    )}
                </div>
            )}
            <div ref={listAreaRef} style={{ flex: 1, minHeight: 0 }}>
                {filteredItems.length === 0 ? (
                    <ScheduledMessagesEmptyState
                        // A filter or a search hiding everything is not the same as
                        // having nothing scheduled, and must not say the same thing —
                        // and neither is "everything already went out", the one empty
                        // list the DEFAULT filters create.
                        variant={(() => {
                            const reason = plannerEmptyReason(filters, unfilteredCount, !!searchToken);
                            return reason === 'nothing-at-all' ? 'empty'
                                : reason === 'sent-hidden' ? 'sent-hidden'
                                    : 'no-matches';
                        })()}
                        onShowSent={() => dispatch(setCalendarSidebarFilters({ ...filters, showSent: true }))}
                        // BE#657 (a) — the planner empty-state CTA carries no time context, like the
                        // FAB; open a TRULY FRESH compose (no now-seed): pick-a-date/time, Send gated.
                        onCreate={() => dispatch(handleNewMessageDialogThunk({ fresh: true }))}
                    />
                ) : dimensions.height > 0 ? (
                    <ScheduleAgainContext.Provider value={scheduleAgainContext}>
                    <List
                        ref={listRef}
                        height={dimensions.height}
                        width={dimensions.width}
                        itemCount={rows.length}
                        itemSize={itemSize}
                        estimatedItemSize={ROW_HEIGHT}
                        itemData={itemData}
                        overscanCount={5}
                        direction={listDirection}
                    >
                        {PlannerVirtualRow}
                    </List>
                    </ScheduleAgainContext.Provider>
                ) : null}
            </div>
            <ScheduleAgainDialog
                // Keyed by what it is open for, so each open starts clean (no date left over from last time).
                key={dialogIds.join(',')}
                messages={dialogMessages}
                onClose={() => setDialogIds([])}
                onDone={() => { setDialogIds([]); leaveSelectMode(); }}
            />
        </div>
    );
}
